import {
  addProject as addProjectAPI,
  deleteModelFromFolder as deleteModelsFromFolderAPI,
  deleteProject as deleteProjectsAPI,
  duplicateProject as duplicateProjectsAPI,
  duplicateTask as duplicateTasksAPI,
  moveModelToFolder,
  updateRowOrderProject,
  updateRowOrderTask,
} from '@api/projects.js';
import toast from '@components/Common/Toast.js';
import ShareModal from '@components/Modals/ShareModal/Index.js';
import ProjectsState, { LoadedFolderIds } from '@states/Projects.js';

export async function addProject(newProjectName, templateId = 'projects') {
  try {
    const response = await addProjectAPI({
      folderName: newProjectName,
      parentFolderId: ProjectsState.projects.rootFolderId || null,
      templateId,
    });
    if (response.status === 'success') {
      const newFolder = {
        id: response.response.id,
        name: newProjectName,
        uid: response.response.creatorId,
        organizationId: response.response.organizationId,
        createdAt: response.response.createdAt,
        updatedAt: response.response.updatedAt,
        metadata: response.response.metadata,
        parentId: response.response.parentFolderId,
        accessType: response.response.accessType,
        sharingId: response.response.sharingId,
        customColumns: response.response.customColumns || [],
        columnValues: response.response.columnValues || [],
        templateId: response.response.templateId || templateId,
      };
      ProjectsState.setProjects({
        ...ProjectsState.projects,
        folders: [...ProjectsState.projects.folders, newFolder],
      });
      LoadedFolderIds.setLoadedFolderIds([
        ...LoadedFolderIds.loadedFolderIds,
        newFolder.id,
      ]);
      toast.success(i18next.t('pages.projects.toast.add_project'));
      return newFolder;
    }
    throw new Error('Failed to create project');
  } catch (error) {
    console.error('Error adding project:', error);
    toast.error(
      error.response || i18next.t('pages.projects.toast.add_project_error'),
    );
    throw error;
  }
}

/** * Duplicate a project
 * @param {Array} projectIds - The IDs of the projects to duplicate.
 * @returns {Promise<boolean>} - Returns true if duplication was successful.
 */
export async function duplicateProject(projectIds) {
  const idsToDuplicate = Array.isArray(projectIds) ? projectIds : [projectIds];
  if (idsToDuplicate.length === 0) return false;

  try {
    const response = await duplicateProjectsAPI(idsToDuplicate);

    if (response.status === 'success' && Array.isArray(response.response)) {
      const allNewFolders = [];
      const allNewTasks = [];
      const duplicatedProjects = response.response;

      for (const duplicatedFolder of duplicatedProjects) {
        const newFolder = {
          id: duplicatedFolder.id,
          name: duplicatedFolder.folderName,
          createdAt: duplicatedFolder.createdAt,
          updatedAt: duplicatedFolder.updatedAt,
          metadata: duplicatedFolder.metadata,
          parentId: duplicatedFolder.parentFolderId,
          accessType: 'edit', // since we could duplicate means we have access
          sharingId: duplicatedFolder.shortSharingId,
          columnValues: duplicatedFolder.columnValues || [],
          customColumns: duplicatedFolder.customColumns || [],
          uid: duplicatedFolder.creatorId,
          organizationId: duplicatedFolder.organizationId,
          isPublic: duplicatedFolder.isPublic,
          isShared: false,
          rowOrder: undefined,
        };

        const newTasks = (duplicatedFolder.cadfiles || []).map(cadfile => ({
          id: cadfile.cadFileID,
          name: cadfile.name || 'Untitled',
          createdAt: cadfile.createdAt,
          updatedAt: cadfile.updatedAt,
          thumbnail: cadfile.thumbnail,
          columnValues: cadfile.columnValues || [],
          parentId: duplicatedFolder.id,
          accessType: 'edit',
          sharingId: cadfile.sharingId,
          uid: cadfile.uid,
          organizationId: duplicatedFolder.organizationId,
          cadfileAccessLevel: cadfile.cadfileAccessLevel,
          commentsEnabled: cadfile.commentsEnabled || false,
          reconstructions: cadfile.reconstructions || [],
          rowOrder: undefined,
        }));

        allNewFolders.push(newFolder);
        allNewTasks.push(...newTasks);
      }

      if (allNewFolders.length > 0) {
        ProjectsState.setProjects({
          ...ProjectsState.projects,
          folders: [...ProjectsState.projects.folders, ...allNewFolders],
          tasks: [...ProjectsState.projects.tasks, ...allNewTasks],
        });
        LoadedFolderIds.setLoadedFolderIds([
          ...LoadedFolderIds.loadedFolderIds,
          ...allNewFolders.map(folder => folder.id),
        ]);
      }

      toast.success(i18next.t('pages.projects.toast.duplicate_project'));
      return true;
    }
    throw new Error('Failed to duplicate project(s)');
  } catch (error) {
    console.error('Error duplicating project(s):', error);
    toast.error(i18next.t('pages.projects.toast.duplicate_project_error'));
    throw error;
  }
}

/** * Duplicate a task
 * @param {Array} cadFileIds - The IDs of the tasks to duplicate.
 * @returns {Promise<boolean>} - Returns true if duplication was successful.
 */
export async function duplicateTask(cadFileIds) {
  const idsToDuplicate = Array.isArray(cadFileIds) ? cadFileIds : [cadFileIds];
  if (idsToDuplicate.length === 0) {
    return false;
  }

  try {
    const firstOriginalTask = ProjectsState.projects.tasks.find(
      task => task.id === idsToDuplicate[0],
    );

    if (!firstOriginalTask) {
      throw new Error('Original task not found in state.');
    }

    const response = await duplicateTasksAPI(idsToDuplicate);

    if (response.status === 'success' && Array.isArray(response.response)) {
      const duplicatedTasksData = response.response;

      const allNewTasks = duplicatedTasksData.map(duplicatedTask => ({
        id: duplicatedTask.cadFileID,
        name: duplicatedTask.name || 'Untitled',
        createdAt: duplicatedTask.createdAt,
        updatedAt: duplicatedTask.updatedAt,
        thumbnail: duplicatedTask.thumbnail,
        columnValues: duplicatedTask.columnValues || [],
        parentId: duplicatedTask.modelFolderId,
        sharingId: duplicatedTask.sharingId,
        uid: duplicatedTask.uid,
        organizationId: firstOriginalTask.organizationId,
        cadfileAccessLevel: duplicatedTask.cadfileAccessLevel,
        commentsEnabled: duplicatedTask.commentsEnabled,
      }));

      if (allNewTasks.length > 0) {
        ProjectsState.setProjects({
          ...ProjectsState.projects,
          tasks: [...ProjectsState.projects.tasks, ...allNewTasks],
        });
      }

      toast.success(i18next.t('pages.projects.toast.duplicate_task'));
      return true;
    }
    throw new Error(response.response || 'Failed to duplicate tasks');
  } catch (error) {
    console.error('Error duplicating tasks:', error);
    toast.error(i18next.t('pages.projects.toast.duplicate_task_error'));
    throw error;
  }
}

export async function deleteProjects(folderIds) {
  const idsToDelete = Array.isArray(folderIds) ? folderIds : [folderIds];
  if (idsToDelete.length === 0) return { success: true, ids: [] };

  try {
    await deleteProjectsAPI(idsToDelete);

    ProjectsState.setProjects({
      ...ProjectsState.projects,
      folders: ProjectsState.projects.folders.filter(
        f => !idsToDelete.includes(f.id),
      ),
    });
    toast.success(i18next.t('pages.projects.toast.delete_project'));

    return { success: true, ids: idsToDelete };
  } catch (error) {
    console.error('Error deleting folder(s):', error);
    toast.error(i18next.t('pages.projects.toast.delete_project_error'));
    throw error;
  }
}

export async function deleteTasks(folderId, cadFileIds) {
  const idsToDelete = Array.isArray(cadFileIds) ? cadFileIds : [cadFileIds];
  if (idsToDelete.length === 0) {
    return { success: true, ids: [] };
  }

  try {
    await deleteModelsFromFolderAPI(folderId, idsToDelete);

    const deletedTasks = ProjectsState.projects.tasks.filter(task =>
      idsToDelete.includes(task.id),
    );

    ProjectsState.setProjects({
      ...ProjectsState.projects,
      tasks: ProjectsState.projects.tasks.filter(
        t => !idsToDelete.includes(t.id),
      ),
    });

    toast.success(i18next.t('pages.projects.toast.delete_task'));

    const sharingId = getSharingId();
    if (sharingId) {
      const wasViewingDeletedTask = deletedTasks.some(
        task => task.sharingId === sharingId,
      );
      if (wasViewingDeletedTask) {
        window.location.href = '/projects';
      }
    }

    return { success: true, ids: idsToDelete };
  } catch (error) {
    console.error('Error deleting task(s):', error);
    toast.error(i18next.t('pages.projects.toast.delete_task_error'));
    throw error;
  }
}

export async function moveTask(sourceFolderId, cadFileId, targetFolderId) {
  try {
    // multiple task move
    if (Array.isArray(cadFileId)) {
      const movePromises = cadFileId.map(id =>
        moveModelToFolder(sourceFolderId, id, targetFolderId),
      );
      await Promise.all(movePromises);

      const updatedTasks = ProjectsState.projects.tasks.map(task => {
        if (cadFileId.includes(task.id)) {
          return {
            ...task,
            parentId: targetFolderId,
          };
        }
        return task;
      });

      ProjectsState.setProjects({
        ...ProjectsState.projects,
        tasks: updatedTasks,
      });

      return {
        success: true,
        sourceFolderId,
        targetFolderId,
        cadFileIds: cadFileId,
        updatedTasks: updatedTasks.filter(task => cadFileId.includes(task.id)),
      };
    }

    // single task move
    await moveModelToFolder(sourceFolderId, cadFileId, targetFolderId);

    const taskToMove = ProjectsState.projects.tasks.find(
      t => t.id === cadFileId,
    );
    if (!taskToMove) {
      throw new Error('Task not found');
    }

    const updatedTask = {
      ...taskToMove,
      parentId: targetFolderId,
    };

    const updatedTasks = ProjectsState.projects.tasks.map(task =>
      task.id === cadFileId ? updatedTask : task,
    );

    ProjectsState.setProjects({
      ...ProjectsState.projects,
      tasks: updatedTasks,
    });

    toast.success(i18next.t('pages.projects.toast.move_task'));
    return {
      success: true,
      sourceFolderId,
      targetFolderId,
      cadFileId,
      updatedTask,
    };
  } catch (error) {
    console.error('Error moving task:', error);
    toast.error(i18next.t('pages.projects.toast.move_task_error'));
    throw error;
  }
}

export async function shareProjectTask(item) {
  const stateItem = item.sharingId
    ? ProjectsState.projects.tasks.find(task => task.id === item.id)
    : ProjectsState.projects.folders.find(folder => folder.id === item.id);

  const shareModal = new ShareModal(
    stateItem,
    item.sharingId ? 'task' : 'project',
  );
  shareModal.promptAsync(
    document.getElementById('main-layout') ||
      document.getElementById('viewport'),
  );
}

/**
 * Get the shortSharingId from URL parameters or stored value -> shortSharingId is related to a project
 */
export function getSharedProjectId() {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('projectId');
}

/**
 * Check and return the sharing ID from URL parameters - used to know if we are on viewer page -> SharingId is related to a task / cadfile
 */
export function getSharingId() {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('sharingId') || false;
}

/**
 * Get the folder ID from URL parameters - used for direct navigation to a folder
 * @returns {string|null} The folder ID from URL or null if not present
 */
export function getFolderIdFromUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  return parseInt(urlParams.get('pId')) || null;
}

/**
 * Updates the rowOrder property for items in an array after a drag-and-drop operation.
 * @param {Array<Object>} items - The array of items to update (e.g., folders, tasks). Each item must have 'id' and 'rowOrder' properties.
 * @param {string|number} itemId - The ID of the item that was moved.
 * @param {number} newPosition - The new rowOrder for the moved item.
 * @param {number} originalPosition - The original rowOrder of the moved item.
 * @param {boolean} isTask - Whether the items are tasks (true) or folders (false).
 */
export const updateRowOrders = async ({
  items,
  itemId,
  newPosition,
  originalPosition,
  isTask,
  idIndexList,
}) => {
  // Update the row orders in the provided items array
  items.forEach(item => {
    if (item.id === itemId) {
      // Update the dragged item to its new position
      item.rowOrder = newPosition;
    } else if (
      originalPosition > newPosition &&
      item.rowOrder >= newPosition &&
      item.rowOrder < originalPosition
    ) {
      // Item was moved UP, so shift items between the new and old positions DOWN
      item.rowOrder += 1;
    } else if (
      originalPosition < newPosition &&
      item.rowOrder <= newPosition &&
      item.rowOrder > originalPosition
    ) {
      // Item was moved DOWN, so shift items between the old and new positions UP
      item.rowOrder -= 1;
    }
  });

  // Update ProjectsState with the modified items
  const currentState = ProjectsState.projects;

  if (isTask) {
    // Update tasks in ProjectsState
    const updatedTasks = currentState.tasks.map(task => {
      const updatedItem = items.find(item => item.id === task.id);
      return updatedItem || task;
    });

    ProjectsState.setProjects({
      ...currentState,
      tasks: updatedTasks,
    });
  } else {
    // Update folders in ProjectsState
    const updatedFolders = currentState.folders.map(folder => {
      const updatedItem = items.find(item => item.id === folder.id);
      return updatedItem || folder;
    });

    ProjectsState.setProjects({
      ...currentState,
      folders: updatedFolders,
    });
  }

  // API call to update the row order on the server
  try {
    if (isTask) {
      await updateRowOrderTask(itemId, {
        newOrder: newPosition,
        newIndexList: idIndexList,
      });
    } else {
      await updateRowOrderProject(itemId, {
        newOrder: newPosition,
        newIndexList: idIndexList,
      });
    }
  } catch (apiError) {
    console.error(
      `Failed to update ${isTask ? 'task' : 'project'} row order on server:`,
      apiError,
    );
    toast.error(`Failed to update ${isTask ? 'task' : 'project'} order`);
  }
};
